import {OidcService} from '~/openapi';

// Keyed by Nuxt app instance: on the server each SSR request has its own app,
// on the client there is one app for the whole session. Guarantees the policy
// runs at most once at a time per app even when several requests fail with
// 401 concurrently.
const inFlightByApp = new WeakMap<object, Promise<void>>();

/**
 * Global 401 policy shared by client and SSR.
 *
 * A 401 from the backend means the session cookie is no longer valid. The
 * reaction is the same in both environments: downgrade the app to guest state
 * (clear auth state and protected caches) and never crash the render because
 * of it. On the client we additionally invalidate the stale session cookie
 * server-side; during SSR we must not mutate the session (no request context
 * for Set-Cookie through axios) — the client interceptor finishes the cleanup
 * after hydration if the session is still stale.
 */
export default function useUnauthorizedPolicy() {
	const nuxtApp = useNuxtApp();

	function handleUnauthorized(): Promise<void> {
		const existing = inFlightByApp.get(nuxtApp);
		if (existing) {
			return existing;
		}

		const handling = nuxtApp.runWithContext(async () => {
			if (import.meta.client) {
				try {
					// Invalidate the stale session cookie server-side (returns 204).
					await OidcService.logoutSession({});
				} catch {
					// Session is already gone, nothing to clean up server-side.
				}
			}

			useSessionActions().clearLocalSession();
		}).finally(() => {
			inFlightByApp.delete(nuxtApp);
		});

		inFlightByApp.set(nuxtApp, handling);

		return handling;
	}

	return {handleUnauthorized};
}
